import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Class, YearGroup } from '../../types';

const classSchema = z.object({
  name: z.string().min(1, 'Class name is required'),
  yearGroupId: z.string().min(1, 'Year group is required'),
  capacity: z.coerce.number().min(1, 'Capacity must be at least 1'),
});

type ClassFormData = z.infer<typeof classSchema>;

interface ClassFormProps {
  classData?: Class;
  yearGroups: YearGroup[];
  onSubmit: (data: Class) => void;
  onCancel: () => void;
}

export const ClassForm: React.FC<ClassFormProps> = ({ classData, yearGroups, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ClassFormData>({
    resolver: zodResolver(classSchema),
    defaultValues: {
      name: classData?.name || '',
      yearGroupId: classData?.yearGroupId || '',
      capacity: classData?.capacity || 30,
    },
  });

  const handleFormSubmit = (data: ClassFormData) => {
    onSubmit({
      ...data,
      id: classData?.id || `class-${Date.now()}`,
      currentSize: classData?.currentSize || 0,
    } as Class);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Class Name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message || 'e.g., 7A, 10B'}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="yearGroupId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.yearGroupId}>
                <InputLabel>Year Group *</InputLabel>
                <Select {...field} label="Year Group *">
                  {yearGroups.map((yg) => (
                    <MenuItem key={yg.id} value={yg.id}>
                      {yg.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.yearGroupId && <FormHelperText>{errors.yearGroupId.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="capacity"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Capacity"
                type="number"
                fullWidth
                error={!!errors.capacity}
                helperText={errors.capacity?.message || 'Maximum number of students'}
                required
              />
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          {classData ? 'Update' : 'Create'} Class
        </Button>
      </Box>
    </form>
  );
};
